import React, { useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { cn } from '../lib/utils';

export type SnackbarType = 'error' | 'success' | 'info';

interface SnackbarProps {
    message: string;
    type?: SnackbarType;
    isOpen: boolean;
    onClose: () => void;
    autoHideDuration?: number;
}

export const Snackbar: React.FC<SnackbarProps> = ({
    message,
    type = 'info',
    isOpen,
    onClose,
    autoHideDuration = 4000
}) => {
    useEffect(() => {
        if (isOpen && autoHideDuration > 0) {
            const timer = setTimeout(() => {
                onClose();
            }, autoHideDuration);
            return () => clearTimeout(timer);
        }
    }, [isOpen, autoHideDuration, onClose]);

    if (!isOpen) return null;

    const icons = {
        error: <AlertCircle size={20} className="text-white" />,
        success: <CheckCircle size={20} className="text-white" />,
        info: <Info size={20} className="text-white" />
    };

    const bgColors = {
        error: 'bg-red-500',
        success: 'bg-emerald-500',
        info: 'bg-slate-800'
    };

    return (
        <div className={cn(
            "fixed bottom-6 right-6 z-[10000] flex items-center gap-3 px-4 py-2 rounded-md shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-300 min-w-[280px] max-w-[400px]",
            bgColors[type]
        )}>
            {icons[type]}
            <span className="text-white text-sm font-medium flex-1">{message}</span>
            <button
                onClick={onClose}
                className="p-1 hover:bg-white/20 rounded-full transition-colors ml-1"
            >
                <X size={14} className="text-white" />
            </button>
        </div>
    );
};
